// src/shared/hooks/useRoom.js
import { useCallback, useEffect, useState } from 'react';
import { useGameContext } from '../context/GameContext';
import {
  createRoom, joinRoom, leaveRoom as fbLeaveRoom, listenOnlineUsers, listenOpenRooms, resetRoom,
} from '../firebase/services';

export function useOnlineUsers() {
  const [users, setUsers] = useState([]);

  useEffect(() => listenOnlineUsers(setUsers), []);

  return { users, count: users.length };
}

export function useOpenRooms() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    return listenOpenRooms((openRooms) => {
      setRooms(openRooms);
      setLoading(false);
    });
  }, []);

  return { rooms, loading };
}

export function useRoom() {
  const { state, setRoomId, leaveRoom: ctxLeave, setLoading, setError, notify } = useGameContext();

  const create = useCallback(async (playerName, settings, gameType = 'drawing') => {
    setLoading(true);
    try {
      const roomId = await createRoom(playerName, settings, gameType);
      setRoomId(roomId);
      notify(`Room ${roomId} created!`);
      return roomId;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setRoomId, setLoading, setError, notify]);

  const join = useCallback(async (roomId, playerName) => {
    setLoading(true);
    try {
      await joinRoom(roomId.toUpperCase(), playerName);
      setRoomId(roomId.toUpperCase());
      notify(`Joined room ${roomId.toUpperCase()}!`);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [setRoomId, setLoading, setError, notify]);

  const leave = useCallback(async () => {
    if (state.roomId && state.userId) {
      const playerName = state.me?.name || state.playerName;
      await fbLeaveRoom(state.roomId, state.userId, playerName);
    }
    ctxLeave();
  }, [state.roomId, state.userId, state.me, state.playerName, ctxLeave]);

  const reset = useCallback(async () => {
    if (!state.roomId || !state.isHost) return;
    await resetRoom(state.roomId, state.userId);
    notify('Game reset!');
  }, [state.roomId, state.userId, state.isHost, notify]);

  return { create, join, leave, reset };
}